import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, postLoginRoute } = useAuth();

  const sessaoExpirada = searchParams.get('sessaoExpirada') === '1';
  const redirect = searchParams.get('redirect');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    try {
      await login(username, password);
      navigate(redirect || postLoginRoute);
    } catch (err: any) {
      setErrorMessage(
        err.response?.data?.message ||
          err.response?.data?.errors?.username?.[0] ||
          'Erro ao autenticar'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-sky-50 via-sky-50 to-indigo-50 p-4">
      <div className="flex w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-xl">
        {/* Painel esquerdo: identidade visual */}
        <div className="hidden w-80 flex-col items-center justify-center gap-6 bg-gradient-to-br from-sky-600 to-sky-800 p-10 text-white md:flex">
          <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2 className="text-center text-xl font-bold">Portal Industrialização</h2>
          <p className="text-center text-sm text-white/75">
            Gestão integrada de processos de industrialização e produção.
          </p>
          <ul className="w-full space-y-3 text-sm text-white/90">
            <li className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15">⚙️</span>
              Ordens de Produção e Apontamentos
            </li>
            <li className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15">📦</span>
              Controle de Estoque e Insumos
            </li>
            <li className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15">📊</span>
              Relatórios e Gestão de Custos
            </li>
          </ul>
        </div>

        {/* Painel direito: formulario */}
        <div className="flex flex-1 flex-col justify-center p-10">
          <h3 className="mb-1 text-xl font-bold text-slate-800">Bem-vindo! 👋🏻</h3>
          <p className="mb-6 text-sm text-slate-500">Faça login em sua conta para continuar.</p>

          {sessaoExpirada && (
            <div className="mb-5 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-700">
              Sua sessão expirou. Faça login novamente para continuar.
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Usuário</label>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Senha</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-2 text-xs text-slate-400"
                >
                  {showPassword ? 'ocultar' : 'mostrar'}
                </button>
              </div>
            </div>

            {errorMessage && (
              <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{errorMessage}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-2 rounded-md bg-sky-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sky-700 disabled:opacity-60"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
